import { ReplaySubject, Subject } from "rxjs";
import { BetfairHistoricEvent } from "../types/betfair/price-matching/betfair-historic-event";
import {
  BetfairHistoricPrice,
  EventInfo,
  PriceMatchingData,
  PriceMatchingWebsocketUpdate,
  WebsocketStatus,
} from "../types/betfair/price-matching/price-matching-data";
import { deserializeCricketEventList } from "../types/entities/cricket-event";
import { Ground } from "../types/entities/ground";
import { Match } from "../types/entities/match";
import { Team } from "../types/entities/team";
import { showMessage } from "../types/services";
import { HttpService } from "./http-service";

export class PriceMatchingService {
  private readonly httpService: HttpService;
  private readonly baseUrl: string = "/api/price-matching-controller";
  public latestWebsocketUpdate: Subject<PriceMatchingWebsocketUpdate> =
    new ReplaySubject(1);

  constructor(httpService: HttpService) {
    this.httpService = httpService;
  }

  public updateBetfairHistoricEvents(): Promise<any> {
    return this.httpService
      .post(this.baseUrl + "/update-historic-data", null)
      .catch((reason) => {
        showMessage(
          `Failed to update betfair historic data: ${reason}`,
          "error"
        );
      });
  }

  public updateBetfairHistoricPrices(): Promise<any> {
    return this.httpService
      .post(this.baseUrl + "/update-historic-prices", null)
      .catch((reason) => {
        showMessage(
          `Failed to update betfair historic prices: ${reason}`,
          "error"
        );
      });
  }

  public getPriceMatchingEvents(): Promise<
    Map<number, Map<string, Map<number, BetfairHistoricEvent[]>>>
  > {
    return this.httpService
      .get(this.baseUrl + "/get-price-matching-events")
      .then((response) => this.deserializeEventMap(response))
      .catch((reason) => {
        showMessage(
          `Failed to get betfair historic data event list: ${reason}`,
          "error"
        );
        return new Map();
      });
  }

  public getPriceMatchingDataForEvent(
    eventName: string
  ): Promise<PriceMatchingData> {
    const params: Map<string, string> = new Map();
    params.set("eventId", eventName);
    return this.httpService
      .get(this.baseUrl + "/get-price-matching-data", params)
      .then((response) => this.deserializePriceMatchingData(response))
      .catch((reason) => {
        showMessage(
          `Failed to get betfair historic data for event ${eventName}: ${reason}`,
          "error"
        );
        return null;
      });
  }

  public deserializePriceMatchingData(response: any): PriceMatchingData {
    return {
      eventInfo: this.deserializeEventInfo(response.eventInfo),
      streamApiEvents: response.streamApiEvents,
      matchedPrices: this.deserializeMatchedPricesMap(response.matchedPrices),
      firstInningsBetfairRunsLineOdds: response.firstInningsBetfairRunsLineOdds,
      firstInningsEreborRunsLineOdds: response.firstInningsEreborRunsLineOdds,
      firstInningsStartTime: response.firstInningsStartTime,
    };
  }

  public deserializeEventInfo(json: any): EventInfo {
    return {
      match: Match.deserializeOne(json.match),
      ground: Ground.deserializeOne(json.ground),
      events: deserializeCricketEventList(json.events),
      team1: Team.deserializeOne(json.team1),
      eventName: json.eventName,
      historicEvent: json.historicEvent,
    };
  }

  public deserializeMatchedPricesMap(
    mapJson: any
  ): Map<string, BetfairHistoricPrice> {
    const map: Map<string, BetfairHistoricPrice> = new Map();
    Object.keys(mapJson).forEach((key) => {
      map.set(key, mapJson[key]);
    });
    return map;
  }

  public deserializeEventMap(
    response: any
  ): Map<number, Map<string, Map<number, BetfairHistoricEvent[]>>> {
    const map: Map<
      number,
      Map<string, Map<number, BetfairHistoricEvent[]>>
    > = new Map();
    Object.keys(response).forEach((year) => {
      map.set(Number(year), new Map());
      Object.keys(response[year]).forEach((month) => {
        map.get(Number(year)).set(month, new Map());
        Object.keys(response[year][month]).forEach((day) => {
          map
            .get(Number(year))
            .get(month)
            .set(Number(day), response[year][month][day]);
        });
      });
    });
    return map;
  }

  public betfairPriceMatchingUpdate(msg: any) {
    const update: PriceMatchingWebsocketUpdate = {
      event: msg.betfairEvent,
      amountDone: msg.amountDone,
      totalSize: msg.totalSize,
      status: WebsocketStatus[msg.status],
    };

    this.latestWebsocketUpdate.next(update);
  }
}
